import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Rocket,
  Users,
  DollarSign,
  Target,
  Building,
  Calendar,
  Clock,
  Award,
  Check,
  TrendingUp,
  Star,
  Shield
} from "lucide-react";
import { trackEvent } from "@/utils/analytics";

const API = process.env.REACT_APP_BACKEND_URL;

// Application sections
const APPLICATION_SECTIONS = [
  {
    id: "business_info",
    title: "About Your Business",
    icon: Building,
    questions: [
      {
        id: "business_name",
        type: "text",
        label: "Business Name",
        placeholder: "Your company name",
        required: true
      },
      {
        id: "industry",
        type: "text",
        label: "Industry / Niche",
        placeholder: "e.g., Marketing Agency, Health Coaching, E-commerce",
        required: true
      },
      {
        id: "years_in_business",
        type: "radio",
        label: "How long have you been in business?",
        options: [
          { value: "1_2_years", label: "1-2 years" },
          { value: "3_5_years", label: "3-5 years" },
          { value: "5_10_years", label: "5-10 years" },
          { value: "10_plus", label: "10+ years" }
        ],
        required: true
      },
      {
        id: "business_model",
        type: "radio",
        label: "Primary business model",
        options: [
          { value: "services", label: "Service-based (consulting, coaching, agency)" },
          { value: "products", label: "Product-based (physical or digital products)" },
          { value: "saas", label: "SaaS / Software" },
          { value: "hybrid", label: "Hybrid (multiple revenue streams)" }
        ],
        required: true
      }
    ]
  },
  {
    id: "revenue_team",
    title: "Revenue & Team",
    icon: DollarSign,
    questions: [
      {
        id: "monthly_revenue",
        type: "radio",
        label: "Current monthly revenue",
        options: [
          { value: "25k_50k", label: "$25,000 - $50,000" },
          { value: "50k_100k", label: "$50,000 - $100,000" },
          { value: "100k_250k", label: "$100,000 - $250,000" },
          { value: "250k_plus", label: "$250,000+" }
        ],
        required: true
      },
      {
        id: "revenue_goal",
        type: "radio",
        label: "12-month revenue goal",
        options: [
          { value: "double", label: "Double current revenue" },
          { value: "500k", label: "Reach $500K/year" },
          { value: "1m", label: "Reach $1M/year" },
          { value: "beyond_1m", label: "Scale beyond $1M/year" }
        ],
        required: true
      },
      {
        id: "team_size",
        type: "radio",
        label: "Current team size (including contractors)",
        options: [
          { value: "solo", label: "Just me" },
          { value: "2_5", label: "2-5 people" },
          { value: "6_15", label: "6-15 people" },
          { value: "15_plus", label: "15+ people" }
        ],
        required: true
      },
      {
        id: "hiring_plans",
        type: "radio",
        label: "Hiring plans in the next 6 months",
        options: [
          { value: "none", label: "No immediate plans" },
          { value: "1_2", label: "1-2 key hires" },
          { value: "3_5", label: "3-5 hires" },
          { value: "building_team", label: "Building a full team" }
        ],
        required: true
      }
    ]
  },
  {
    id: "challenges",
    title: "Current Challenges",
    icon: Target,
    questions: [
      {
        id: "primary_challenge",
        type: "radio",
        label: "What&apos;s your biggest scaling challenge right now?",
        options: [
          { value: "delegation", label: "Difficulty delegating - I'm still doing too much" },
          { value: "systems", label: "Lack of systems - everything depends on me" },
          { value: "leadership", label: "Team management - struggling to lead effectively" },
          { value: "strategy", label: "Strategic clarity - unsure which direction to grow" },
          { value: "operations", label: "Operations - can't handle more volume" },
          { value: "sales", label: "Sales/revenue - plateau or inconsistent growth" }
        ],
        required: true
      },
      {
        id: "challenge_details",
        type: "textarea",
        label: "Describe your current situation in detail",
        placeholder: "What's keeping you up at night? What have you tried? What's not working?",
        required: true
      },
      {
        id: "previous_support",
        type: "checkbox_group",
        label: "What type of support have you tried before? (Select all that apply)",
        options: [
          { value: "coach", label: "Business coach or mentor" },
          { value: "mastermind", label: "Mastermind or peer group" },
          { value: "consultant", label: "Strategy consultant" },
          { value: "courses", label: "Online courses or programs" },
          { value: "books", label: "Books and self-study" },
          { value: "none", label: "This would be my first investment" }
        ],
        required: false
      }
    ]
  },
  {
    id: "commitment",
    title: "Your Commitment",
    icon: Award,
    questions: [
      {
        id: "why_now",
        type: "textarea",
        label: "Why are you seeking strategic support now?",
        placeholder: "What's changed? What opportunity or pressure is driving this decision?",
        required: true
      },
      {
        id: "ideal_outcome",
        type: "textarea",
        label: "What would success look like in 6 months?",
        placeholder: "Be specific - revenue, team, lifestyle, business structure...",
        required: true
      },
      {
        id: "investment_ready",
        type: "radio",
        label: "Are you ready to invest $3,000-$5,000/month for strategic partnership?",
        options: [
          { value: "yes", label: "Yes, I'm ready to invest at this level" },
          { value: "depends", label: "It depends on the fit and value" },
          { value: "stretch", label: "It would be a stretch, but I'm committed" }
        ],
        required: true
      },
      {
        id: "time_commitment",
        type: "radio",
        label: "Can you commit to weekly strategy sessions?",
        options: [
          { value: "yes", label: "Yes, I can make time for weekly sessions" },
          { value: "mostly", label: "Most weeks, with occasional conflicts" },
          { value: "challenging", label: "This would be challenging for me" }
        ],
        required: true
      }
    ]
  },
  {
    id: "contact",
    title: "Contact Information",
    icon: Users,
    questions: [
      {
        id: "name",
        type: "text",
        label: "Your Full Name",
        placeholder: "First and last name",
        required: true
      },
      {
        id: "email",
        type: "email",
        label: "Email Address",
        placeholder: "your@email.com",
        required: true
      },
      {
        id: "phone",
        type: "text",
        label: "Phone Number",
        placeholder: "(555) 123-4567",
        required: false
      },
      {
        id: "website",
        type: "text",
        label: "Website URL (optional)",
        placeholder: "https://yourwebsite.com",
        required: false
      },
      {
        id: "referral_source",
        type: "radio",
        label: "How did you hear about us?",
        options: [
          { value: "assessment", label: "Business Clarity Assessment" },
          { value: "referral", label: "Referral from a friend or colleague" },
          { value: "social", label: "Social media" },
          { value: "search", label: "Google search" },
          { value: "other", label: "Other" }
        ],
        required: true
      }
    ]
  }
];

export const ScaleApplication = () => {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [checkboxAnswers, setCheckboxAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const totalSections = APPLICATION_SECTIONS.length;
  const progress = ((currentSection + 1) / totalSections) * 100;
  const currentSectionData = APPLICATION_SECTIONS[currentSection];

  const handleInputChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleCheckboxChange = (questionId, optionValue, checked) => {
    setCheckboxAnswers(prev => {
      const current = prev[questionId] || [];
      if (checked) {
        return { ...prev, [questionId]: [...current, optionValue] };
      } else {
        return { ...prev, [questionId]: current.filter(v => v !== optionValue) };
      }
    });
  };

  const isCurrentSectionComplete = () => {
    if (!currentSectionData) return true;
    return currentSectionData.questions.every(q => {
      if (!q.required) return true;
      if (q.type === "checkbox_group") return true; // Optional
      const answer = answers[q.id];
      return answer && answer.toString().trim() !== "";
    });
  };

  const handleNext = () => {
    if (currentSection < totalSections - 1) {
      setCurrentSection(prev => prev + 1);
      trackEvent("scale_application_section_complete", { section: currentSectionData.id });
    } else {
      submitApplication();
    }
  };

  const handleBack = () => {
    if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
    }
  };

  const submitApplication = async () => {
    setLoading(true);
    setError(null);
    trackEvent("scale_application_submit_start");

    try {
      const applicationData = {
        ...answers,
        previous_support: checkboxAnswers.previous_support || [],
        submitted_at: new Date().toISOString()
      };

      const response = await fetch(`${API}/api/scale-application`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(applicationData)
      });

      if (response.ok) {
        setSubmitted(true);
        trackEvent("scale_application_complete", {
          revenue: answers.monthly_revenue,
          team_size: answers.team_size
        });
        toast.success("Application submitted successfully!");
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Failed to submit application");
        trackEvent("scale_application_error", { error: errorData.detail });
      }
    } catch (err) {
      setError("Unable to connect. Please try again.");
      trackEvent("scale_application_error", { error: "network_error" });
    } finally {
      setLoading(false);
    }
  };

  const renderQuestion = (question) => {
    const value = answers[question.id] || "";

    switch (question.type) {
      case "text":
      case "email":
        return (
          <div key={question.id} className="space-y-2">
            <Label className="text-base font-medium text-neutral-900">
              {question.label}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type={question.type}
              value={value}
              onChange={(e) => handleInputChange(question.id, e.target.value)}
              placeholder={question.placeholder}
              className="h-12 text-base"
              data-testid={`input-${question.id}`}
            />
          </div>
        );

      case "textarea":
        return (
          <div key={question.id} className="space-y-2">
            <Label className="text-base font-medium text-neutral-900">
              {question.label}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              value={value}
              onChange={(e) => handleInputChange(question.id, e.target.value)}
              placeholder={question.placeholder}
              className="min-h-[120px] text-base"
              data-testid={`textarea-${question.id}`}
            />
          </div>
        );

      case "radio":
        return (
          <div key={question.id} className="space-y-3">
            <Label className="text-base font-medium text-neutral-900">
              {question.label}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <RadioGroup
              value={value}
              onValueChange={(val) => handleInputChange(question.id, val)}
              className="space-y-2"
            >
              {question.options.map((option) => (
                <div
                  key={option.value}
                  className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                    value === option.value
                      ? "border-[#39FF14] bg-[#39FF14]/5"
                      : "border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50"
                  }`}
                  onClick={() => handleInputChange(question.id, option.value)}
                >
                  <RadioGroupItem value={option.value} id={`${question.id}-${option.value}`} />
                  <Label
                    htmlFor={`${question.id}-${option.value}`}
                    className="flex-1 cursor-pointer text-sm"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );

      case "checkbox_group":
        return (
          <div key={question.id} className="space-y-3">
            <Label className="text-base font-medium text-neutral-900">
              {question.label}
            </Label>
            <div className="space-y-2">
              {question.options.map((option) => {
                const isChecked = (checkboxAnswers[question.id] || []).includes(option.value);
                return (
                  <div
                    key={option.value}
                    className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                      isChecked
                        ? "border-[#39FF14] bg-[#39FF14]/5"
                        : "border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50"
                    }`}
                    onClick={() => handleCheckboxChange(question.id, option.value, !isChecked)}
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={(checked) => handleCheckboxChange(question.id, option.value, checked)}
                    />
                    <Label className="flex-1 cursor-pointer text-sm">
                      {option.label}
                    </Label>
                  </div>
                );
              })}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Success View
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#39FF14]/20 rounded-full mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#39FF14]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-neutral-900 mb-4">
            Application Received!
          </h1>
          <p className="text-lg text-neutral-600 mb-6">
            Thank you for applying to the Scale & Leadership Retainer, {answers.name?.split(" ")[0] || ""}!
          </p>
          
          <Card className="mb-8 text-left">
            <CardContent className="p-6">
              <h3 className="font-semibold text-neutral-900 mb-4">What happens next?</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#39FF14]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-semibold text-[#39FF14]">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">Application Review</p>
                    <p className="text-sm text-neutral-600">We&apos;ll review your application within 24-48 hours</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#39FF14]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-semibold text-[#39FF14]">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">Discovery Call</p>
                    <p className="text-sm text-neutral-600">If there&apos;s a potential fit, we&apos;ll schedule a call to discuss your goals</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#39FF14]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-semibold text-[#39FF14]">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">Strategic Partnership</p>
                    <p className="text-sm text-neutral-600">If we&apos;re aligned, we&apos;ll begin your strategic scaling journey</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate("/")}
              className="bg-neutral-900 hover:bg-neutral-800 text-white"
            >
              Return to Home
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/pricing")}
            >
              View All Programs
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Loading View
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#39FF14] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">Submitting Your Application...</h2>
          <p className="text-neutral-600">Please wait while we process your information.</p>
        </div>
      </div>
    );
  }

  const SectionIcon = currentSectionData?.icon || Building;

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/pricing")}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Pricing
          </Button>
          
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 mb-4">
            <Award className="w-3 h-3 mr-1" />
            Application Only
          </Badge>
          
          <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-900 mb-2">
            Scale & Leadership Retainer Application
          </h1>
          <p className="text-neutral-600">
            $3,000 - $5,000/month • Strategic Partnership for Intentional Scaling
          </p>
        </div>

        {/* What&apos;s Included Preview */}
        {currentSection === 0 && (
          <Card className="mb-6 border-amber-200 bg-amber-50/50">
            <CardContent className="p-6">
              <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500" />
                What&apos;s Included in the Scale & Leadership Retainer
              </h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-2 text-sm text-neutral-700">
                  <Check className="w-4 h-4 text-amber-600" />
                  Weekly strategic advisory sessions
                </div>
                <div className="flex items-center gap-2 text-sm text-neutral-700">
                  <Check className="w-4 h-4 text-amber-600" />
                  Full business operating system dashboard
                </div>
                <div className="flex items-center gap-2 text-sm text-neutral-700">
                  <Check className="w-4 h-4 text-amber-600" />
                  Custom AI workflows & SOPs
                </div>
                <div className="flex items-center gap-2 text-sm text-neutral-700">
                  <Check className="w-4 h-4 text-amber-600" />
                  Team planning & leadership support
                </div>
                <div className="flex items-center gap-2 text-sm text-neutral-700">
                  <Check className="w-4 h-4 text-amber-600" />
                  Quarterly strategic reviews
                </div>
                <div className="flex items-center gap-2 text-sm text-neutral-700">
                  <Check className="w-4 h-4 text-amber-600" />
                  High-level decision support
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-neutral-500 mb-2">
            <span>Section {currentSection + 1} of {totalSections}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Section Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <SectionIcon className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <CardTitle>{currentSectionData?.title}</CardTitle>
                <CardDescription>
                  {currentSection === 0 && "Tell us about your business"}
                  {currentSection === 1 && "Understanding your scale"}
                  {currentSection === 2 && "Identifying opportunities"}
                  {currentSection === 3 && "Your commitment to growth"}
                  {currentSection === 4 && "How to reach you"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {currentSectionData?.questions.map(renderQuestion)}
          </CardContent>
        </Card>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentSection === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={handleNext}
            disabled={!isCurrentSectionComplete()}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="application-next-btn"
          >
            {currentSection === totalSections - 1 ? (
              <>
                Submit Application
                <Rocket className="w-4 h-4 ml-2" />
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>

        {/* Trust indicators */}
        <div className="mt-8 text-center text-sm text-neutral-500">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-4 h-4" />
            <p>Your information is private and secure</p>
          </div>
          <p>We review all applications personally • Response within 24-48 hours</p>
        </div>
      </div>
    </div>
  );
};

export default ScaleApplication;
